import { Badge } from "@/scott/ui/elements/badge/badge";
import { Button } from "@/scott/ui/elements/button/button";
import { Divider } from "@/scott/ui/elements/divider/divider";
import { Price } from "@/scott/ui/elements/price/price";
import { TextD2C } from "@/scott/ui/elements/text-d2c/text";
import { cn } from "@/scott/ui/utils";
import { ProductIncludedProductsSection } from "@/ui/pages/product-detail-page/components/product-included-products-section";
import { ProductSizeFinderFitanalytics } from "@/ui/pages/product-detail-page/components/product-size-finder-fitanalytics";
import { ProductSizeFinderSmartfit } from "@/ui/pages/product-detail-page/components/product-size-finder-smartfit";
import type { Dictionary } from "@/utils/dictionaries/get-dictionary";
import type {
  ProductDetailPageViewModel,
  ProductDetailSizeStockMessageType,
  ProductSizeFinderConfig,
} from "../product-detail-page.adapter";
import type { stockNotificationAction } from "../stock-notification-action";
import { ProductAddToCartButton } from "./product-add-to-cart-button";
import { ProductColorSelector } from "./product-color-selector";
import { productSummaryScrollAttributes } from "./product-hero-scroll-pin.constants";
import { ProductKeyFeatureList } from "./product-key-feature-list";
import { ProductMetaList } from "./product-meta-list";
import { ProductSizeChart } from "./product-size-chart";
import { ProductSizeSelector } from "./product-size-selector";
import { ProductStockNotificationInline } from "./product-stock-notification-inline";
import { ProductStoreFinderLocally } from "./product-store-finder-locally";
import { ProductStoreFinderModal } from "./product-store-finder-modal";

export interface ProductSummaryPanelProps {
  product: ProductDetailPageViewModel;
  dictionary: Dictionary["productDetailPage"];
  country: string;
  isEcommerce: boolean;
  stockNotificationAction: typeof stockNotificationAction;
  language: string;
  newBadgeLabel?: string;
  sizeFinder: ProductSizeFinderConfig;
}

function getSizeStockMessage(
  type: ProductDetailSizeStockMessageType | undefined,
  dictionary: Dictionary["productDetailPage"]
): string | undefined {
  if (type === "onlyOneLeft") {
    return dictionary.onlyOneLeft;
  }
  if (type === "onlyTwoLeft") {
    return dictionary.onlyTwoLeft;
  }
  if (type === "lowStock") {
    return dictionary.lowStock;
  }
}

export function ProductSummaryPanel(props: Readonly<ProductSummaryPanelProps>) {
  const { dictionary, product } = props;
  const showBidex =
    product.stockStatus !== undefined && Boolean(product.bidexIframeUrl);
  const showLocally =
    product.stockStatus !== undefined && Boolean(product.locallyParams);
  const showStoreFinder = showBidex || showLocally;
  const showStockNotification = product.stockStatus === "availableInFuture";
  const showGenericDealerButton =
    product.stockStatus !== "inStock" &&
    !showStoreFinder &&
    !showStockNotification;
  const showAddToCart = props.isEcommerce && product.stockStatus === "inStock";
  const hasIncludedProducts =
    product.includedProducts && product.includedProducts.length > 0;
  const { showSmartfit, showFitAnalytics } = props.sizeFinder;

  return (
    <aside className="w-full bg-white lg:w-81.25 lg:self-start">
      {/* The gallery controls have 16px of bottom padding. */}
      <div className="flex h-full flex-col lg:h-[max(0px,calc(var(--pdp-gallery-height)-1rem))]">
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          {...productSummaryScrollAttributes}
        >
          <div>
            {props.newBadgeLabel && (
              <span
                className={cn(
                  "mb-2 inline-flex rounded-full px-3 py-1.5",
                  "bg-neonchartreuse"
                )}
              >
                <TextD2C variant="pillar">{props.newBadgeLabel}</TextD2C>
              </span>
            )}

            <TextD2C dash tagName="h1" variant="h3">
              {product.name}
            </TextD2C>
            {product.savings && (
              <div className="mb-2">
                <Badge
                  className="!h-auto min-h-5 whitespace-normal bg-black py-1 uppercase"
                  fontWeight="bold"
                  text={`${product.savings.percentage}% - ${dictionary.youSave} ${product.savings.amount}`}
                  variant="default"
                />
              </div>
            )}
            {product.price && (
              <Price
                price={product.price}
                size="large"
                strikePrice={product.strikePrice}
              />
            )}
          </div>

          {product.keyFeatures.length > 0 && (
            <>
              <Divider className="my-6" />
              <section className="space-y-3">
                <TextD2C style="bold" variant="p-small">
                  {dictionary.keyFeatures}
                </TextD2C>
                <ProductKeyFeatureList features={product.keyFeatures} />
              </section>
            </>
          )}

          {product.colorOptions.length > 0 && (
            <>
              <Divider className="my-6" />
              <ProductColorSelector
                label={dictionary.color}
                options={product.colorOptions}
                value={product.color}
              />
            </>
          )}

          {product.sizeOptions.length > 0 && (
            <>
              <Divider className="my-6" />
              <ProductSizeSelector
                columns={4}
                label={dictionary.size}
                options={product.sizeOptions.map((opt) => ({
                  ...opt,
                  stockMessage: getSizeStockMessage(
                    opt.stockMessageType,
                    dictionary
                  ),
                }))}
                statusBadgeLabels={{
                  availableInFuture: dictionary.stocks.availableInFuture,
                  inStock: dictionary.stocks.inStock,
                  outOfStock: dictionary.stocks.outOfStock,
                }}
                value={product.size}
              />
            </>
          )}

          {product.sizeChart && (
            <ProductSizeChart
              key={product.productCode}
              microcopies={{
                close: dictionary.close,
                linkLabel: dictionary.sizeChart,
              }}
              sizeChart={product.sizeChart}
            />
          )}

          {showSmartfit && (
            <ProductSizeFinderSmartfit
              language={props.language}
              microcopies={{
                title: dictionary.sizeFinder.title,
                description: dictionary.sizeFinder.descriptionSmartfit,
                linkLabel: dictionary.sizeFinder.linkLabel,
                consentMessage: dictionary.sizeFinder.consentMessageSmartfit,
                consentActionLabel: dictionary.sizeFinder.consentActionLabel,
              }}
              productData={{
                name: product.name,
                ean: product.ean,
                imageUrl: product.mediaGallery.mainAsset,
                price: product.priceValue,
                productType: props.sizeFinder.productType,
                apiKey: props.sizeFinder.smartfitApiKey,
                units: props.sizeFinder.measurementSystem,
              }}
              sizeOptions={product.sizeOptions}
            />
          )}

          {showFitAnalytics && (
            <ProductSizeFinderFitanalytics
              country={props.country}
              language={props.language}
              microcopies={{
                title: dictionary.sizeFinder.title,
                description: dictionary.sizeFinder.descriptionFitAnalytics,
                consentMessage:
                  dictionary.sizeFinder.consentMessageFitAnalytics,
                consentActionLabel: dictionary.sizeFinder.consentActionLabel,
              }}
              productData={{
                productCode: product.productCode,
                baseProductCode: product.baseProduct ?? product.productCode,
                name: product.name,
                priceValue: product.priceValue,
                category: product.category,
              }}
            />
          )}

          {hasIncludedProducts && (
            <>
              <Divider className="my-6" />
              <ProductIncludedProductsSection
                microcopies={{
                  title: dictionary.lensIncluded,
                }}
                products={product.includedProducts}
              />
            </>
          )}

          {showBidex && product.bidexIframeUrl && (
            <>
              <Divider className="my-6" />
              <ProductStoreFinderModal
                iframeUrl={product.bidexIframeUrl}
                microcopies={{
                  buttonLabel: dictionary.dealerLocator,
                  buttonSubtitle: dictionary.checkDealerStock,
                  title: dictionary.dealerLocator,
                }}
              />
            </>
          )}

          {showLocally && product.locallyParams && (
            <>
              <Divider className="my-6" />
              <ProductStoreFinderLocally
                buttonLabel={dictionary.dealerLocator}
                buttonSubtitle={dictionary.checkDealerStock}
                locallyParams={product.locallyParams}
              />
            </>
          )}

          {showStockNotification && (
            <>
              <Divider className="my-6" />
              <ProductStockNotificationInline
                action={props.stockNotificationAction}
                dealerLocatorLabel={dictionary.dealerLocator}
                dealerLocatorUrl={product.dealerLocatorUrl}
                key={product.productCode}
                language={props.language}
                microcopies={{
                  title: dictionary.stockNotificationOutOfStockTitle,
                  notificationTitle: dictionary.stockNotificationInlineTitle,
                  emailLabel: dictionary.stockNotificationEmailLabel,
                  emailPlaceholder:
                    dictionary.stockNotificationEmailPlaceholder,
                  submitLabel: dictionary.stockNotificationSubmit,
                  successMessage: dictionary.stockNotificationSuccess,
                  errorMessage: dictionary.stockNotificationError,
                }}
                productCode={product.productCode}
              />
            </>
          )}

          {showGenericDealerButton && (
            <>
              <Divider className="my-6" />
              <Button
                className="h-[71px] w-full flex-col items-start justify-between rounded border border-alabastergrey p-3"
                href={product.dealerLocatorUrl}
                target="_blank"
                variant="outline"
              >
                <span className="font-bold font-brand text-[12px] text-black leading-[14px] tracking-[0.02em]">
                  {dictionary.dealerLocator}
                </span>
                <span className="font-brand font-normal text-[12px] text-black leading-[17px] tracking-normal">
                  {dictionary.checkDealerStock}
                </span>
              </Button>
            </>
          )}

          <Divider className="my-6" />
          <ProductMetaList
            items={[
              {
                label: dictionary.productCode,
                value: product.baseProduct ?? product.productCode,
              },
            ]}
          />
        </div>

        {showAddToCart && (
          <div
            className="fixed inset-x-0 bottom-[var(--site-navigation-bottom-offset,0px)] z-30 shrink-0 border-alabastergrey border-t bg-white p-4 lg:static lg:z-auto"
            data-pdp-cart-bar
          >
            <ProductAddToCartButton
              buttonLabel={dictionary.addToCart}
              country={props.country}
              fallbackErrorMessage={dictionary.errorLoadingProduct}
              gtm={product.gtm}
              key={product.productCode}
              language={props.language}
              productCode={product.productCode}
              stockStatus={product.stockStatus}
              successMessage={dictionary.addToCartSuccess}
              unconfirmedErrorMessage={dictionary.cartUpdateUnconfirmed}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
